<template>
  <div class="animated fadeIn">
    <div class="kt-portlet kt-portlet--mobile" style="position:relative">
      <div class="kt-portlet__body kt-portlet__body--fit" ref="cnt">
        <div class="m-4">
          <div class="filters-container">
            <TableFilters v-model="filterOptions" style="margin-right: 45px" @click="refresh" />
            <DropdownColumns
              v-model="fields"
              class="customize"
              :export-link="exportLink"
              :export-json="exportJson"
            />
          </div>
          <div style="overflow:auto">
            <b-table
              id="dynamic-table"
              :busy.sync="isBusy"
              show-empty
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :hover="true"
              :striped="true"
              stacked="md"
              v-model="copy"
              :items="provider"
              :fields="fields"
              :current-page="currentPage"
              :per-page="perPage"
              @row-clicked="rowClicked"
              tbody-tr-class="tr-pointer"
              v-columns-resizable="{afterResize: columnsWidthChanged}"
            >
              <template v-slot:empty="scope">
                <div class="text-center">{{ $t(scope.emptyText) }}</div>
              </template>
              <template v-slot:head(system_name)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(schedule_time)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(actual_time)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(blocked)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(canceled)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(offered)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(odds)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(custom_odds)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(results)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(results_created)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(results_modified)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(automatic_processing)="data">{{ $t(data.label) }}</template>
              <template v-slot:head(processed)="data">{{ $t(data.label) }}</template>

              <template v-slot:cell(schedule_time)="data">
                <span
                  :class="{'text-danger': data.item.schedule_time != data.item.actual_time}"
                >{{ mom_unix(data.value).format(tableDateMedium) }}</span>
              </template>
              <template v-slot:cell(actual_time)="data">
                <span
                  :class="{'text-danger': data.item.schedule_time != data.item.actual_time}"
                >{{ mom_unix(data.value).format(tableDateMedium) }}</span>
              </template>

              <template v-slot:cell(results_created)="data">
                <span v-if="data.value">{{ mom_unix(data.value).format(tableDateMedium) }}</span>
              </template>
              <template v-slot:cell(results_modified)="data">
                <span v-if="data.value">{{ mom_unix(data.value).format(tableDateMedium) }}</span>
              </template>

              <template v-slot:cell(system_name)="data">
                <router-link :to="`/lottery-systems/${data.item.esystem_id}`">{{data.value}}</router-link>
              </template>
            </b-table>
          </div>
          <div class="container-fluid">
            <div class="row">
              <div style="white-space:nowrap;" class="my-2">
                <PaginationSelect
                  v-model="perPage"
                  :options="paginationOpts"
                  variant="light"
                  size="sm"
                />
                <span>
                  &nbsp;
                  <i18n path="Showing {start}-{end} of {total} rows" tag="span">
                    <span slot="start">{{(currentPage-1) * perPage + 1}}</span>
                    <span slot="end">{{currentPage * perPage}}</span>
                    <span slot="total">{{totalRows}}</span>
                  </i18n>
                </span>
                <b-button
                  size="sm"
                  @click.stop="$root.$emit('bv::refresh::table', 'dynamic-table')"
                  class="ml-2 refresh"
                  variant="light"
                  :title="$t('Refresh')"
                >
                  <i class="icon-refresh"></i>
                </b-button>
              </div>
              <nav class="ml-auto mt-2">
                <b-pagination
                  size="md"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  v-model="currentPage"
                />
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import startCase from "@/shared/strings";
import { errorConf } from "@/shared/notifications";
import tableMixin from "@/shared/mixins/table.mixin";
import PaginationSelect from "@/components/PaginationSelect";
import DropdownColumns from "@/components/DropdownColumns";
import TableFilters from "@/components/TableFilters";

export default {
  name: "lotteryResults",
  mixins: [tableMixin],
  components: {
    PaginationSelect,
    DropdownColumns,
    TableFilters
  },
  data: function() {
    return {
      tableName: "lotteryResults",
      filterOptions: [
        {
          field: "actual_time",
          type: "date",
          opens: "right",
          selected: {
            startDate: moment().startOf("day"),
            endDate: moment().endOf("day")
          },
          default: {
            startDate: moment().startOf("day"),
            endDate: moment().endOf("day")
          },
          ranges: {
            "Last hour": [moment().subtract(1, "hour"), moment()],
            "Last 6 hours": [moment().subtract(6, "hours"), moment()],
            "Last 24 hours": [moment().subtract(24, "hours"), moment()],
            Today: [moment().startOf("day"), moment().endOf("day")],
            Yesterday: [
              moment()
                .subtract(1, "days")
                .startOf("day"),
              moment()
                .subtract(1, "days")
                .endOf("day")
            ],
            "This week": [
              moment().startOf("isoWeek"),
              moment().endOf("isoWeek")
            ],
            "Last week": [
              moment()
                .subtract(1, "week")
                .startOf("isoWeek"),
              moment()
                .subtract(1, "week")
                .endOf("isoWeek")
            ],
            "This month": [moment().startOf("month"), moment().endOf("month")],
            "Last month": [
              moment()
                .subtract(1, "month")
                .startOf("month"),
              moment()
                .subtract(1, "month")
                .endOf("month")
            ],
            "This year": [moment().startOf("year"), moment().endOf("year")],
            "Last year": [moment().subtract(1, "year"), moment()]
          }
        }
      ],
      isBusy: false,
      sortBy: "actual_time",
      sortDesc: true,
      fields: [
        {
          key: "eid",
          label: "Id",
          sortable: false,
          class: "d-none",
          ignore: true
        },
        { key: "code", sortable: true, class: "" },
        {
          key: "esystem_id",
          label: "System Id",
          sortable: true,
          class: "d-none",
          ignore: true
        },
        { key: "system_name", sortable: true, class: "" },
        { key: "schedule_time", sortable: true, class: "", ignore: true },
        { key: "actual_time", sortable: true, class: "d-none", ignore: true },
        { key: "blocked", sortable: true, class: "d-none", ignore: true },
        { key: "canceled", sortable: true, class: "d-none", ignore: true },
        { key: "offered", sortable: true, class: "d-none", ignore: true },
        {
          key: "automatic_processing",
          sortable: true,
          class: "d-none",
          ignore: true
        },
        { key: "processed", sortable: true, class: "", ignore: true },
        { key: "odds", sortable: false, class: "d-none", ignore: true },
        { key: "custom_odds", sortable: false, class: "d-none", ignore: true },
        {
          key: "max_liability",
          sortable: false,
          class: "d-none",
          ignore: true
        },
        {
          key: "custom_liability",
          sortable: false,
          class: "d-none",
          ignore: true
        },
        { key: "results", sortable: false, class: "", ignore: true },
        {
          key: "results_created",
          sortable: false,
          class: "d-none",
          ignore: true
        },
        {
          key: "results_modified",
          sortable: false,
          class: "d-none",
          ignore: true
        }
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filter: null,
      filterField: null,
      checkAll: false,
      copy: null,
      addLabel: ""
    };
  },
  computed: {
    lang() {
      return this.$store.getters["global/defLanguage"];
    },
    exportLink() {
      const apiUrl = this.$store.getters["global/apiUrl"];
      const endpoint = this.$store.getters["lotteryResult/endpoint"];

      return `${apiUrl}/${endpoint}?export=xls&lang=${this.lang}`;
    },
    exportJson() {
      const fields = [];
      this.fields.forEach(item => {
        if (
          typeof item.class != "undefined" &&
          item.class.indexOf("d-none") == -1
        ) {
          let key = item.key;
          if (key != "selected") {
            const efields = ["eid", "esystem_id"];

            if (efields.indexOf(key) > -1) {
              key = key.substr(1);
            }

            fields.push(key);
          }
        }
      });

      const parms = {
        export: "xls",
        lang: this.lang,
        fields: fields.join(",")
      };

      this.filterOptions.forEach(item => {
        if (item.type != "date" && item.selected.length) {
          parms[item.field] = item.selected.join(",");
        } else if (item.type == "date") {
          if (item.selected.startDate && item.selected.endDate) {
            parms.dateField = item.field;
            parms.dateStart = item.selected.startDate;
            parms.dateEnd = item.selected.endDate;
          } else {
            parms.dateField = item.field;
            parms.dateStart = moment().startOf("month");
            parms.dateEnd = moment().endOf("month");
          }
        }
      });

      const paginationData = this.buildPagination();

      paginationData.fields = fields;

      return JSON.stringify(paginationData);
    }
  },
  methods: {
    refresh() {
      this.$root.$emit("bv::refresh::table", "dynamic-table");
    },
    buildPagination() {
      const paginationData = {
        rowsPerPage: this.perPage,
        page: this.currentPage
      };

      if (this.filter && this.filterField) {
        paginationData.likeValues = [this.filter];
        paginationData.likeFields = [this.filterField];
      } else {
        paginationData.likeValues = [];
        paginationData.likeFields = [];
      }

      if (this.sortBy) {
        let sortBy = this.sortBy;
        if (sortBy == "esystem_id") sortBy = "system_id";
        paginationData.sortBy = [sortBy];
        paginationData.sortDirection = [this.sortDesc ? "DESC" : "ASC"];
      }

      const exactFields = [];
      const exactValues = [];

      this.filterOptions.forEach(item => {
        if (item.type != "date" && item.selected.length) {
          exactFields.push(item.field);
          exactValues.push(item.selected);
        } else if (item.type == "date") {
          if (item.selected.startDate && item.selected.endDate) {
            paginationData.dateField = item.field;
            paginationData.dateStart = item.selected.startDate;
            paginationData.dateEnd = item.selected.endDate;
          } else {
            paginationData.dateField = item.field;
            paginationData.dateStart = moment().startOf("day");
            paginationData.dateEnd = moment().endOf("day");
          }
        }
      });

      if (exactFields.length) {
        paginationData.exactFields = exactFields;
        paginationData.exactValues = exactValues;
      }
      return paginationData;
    },
    provider(ctx, callback) {
      const loading = this.$loading.open({
        container: this.$refs.cnt
      });

      const paginationData = this.buildPagination();

      this.$store
        .dispatch("lotteryResult/fetch", paginationData)
        .then(resp => {
          this.totalRows = resp.data.total;
          resp.data.rows.forEach((item, index) => {
            resp.data.rows[index]["_cellVariants"] = {};
            if (item.blocked) {
              resp.data.rows[index]["_cellVariants"]["blocked"] = "danger";
            }
            if (item.canceled) {
              resp.data.rows[index]["_cellVariants"]["canceled"] = "info";
            }
            if (!item.offered) {
              resp.data.rows[index]["_cellVariants"]["offered"] = "danger";
            } else {
              resp.data.rows[index]["_cellVariants"]["offered"] = "success";
            }
            if (item.processed) {
              resp.data.rows[index]["_cellVariants"]["processed"] = "success";
            }
            if (item.automatic_processing) {
              resp.data.rows[index]["_cellVariants"]["automatic_processing"] =
                "success";
            }
          });
          callback(resp.data.rows);
        })
        .catch(err => {
          this.$bvToast.toast(
            this.$t(
              err.details && err.details.length ? err.details[0] : err.message
            ),
            errorConf(this.$t("Error"))
          );
          this.totalRows = 0;
          callback([]);
        })
        .finally(() => {
          loading.close();
        });

      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },
    rowClicked(item, index, event) {
      //this.$router.push("/lottery-results/" + item.eid);
    },
    mom_unix(val) {
      return moment.unix(val / 1e9);
    },
    redirect() {}
  }
};
</script>

<style lang="scss" scoped>
</style>


